import { useState } from "react";
import { LocalizedLink as Link } from "@/components/LocalizedLink";
import {
  ArrowRight, ArrowLeft, Star, Users, BedDouble, Bath, Maximize,
  Car, KeyRound, Clock, MapPin, Mountain, ShieldCheck, Home, Expand, Sun,
  Baby, Coffee, DoorOpen, Heart, ParkingCircle, PawPrint, TreePine, Tv, Utensils, WashingMachine, Waves, Wifi, Wind,
} from "lucide-react";
import { useLang } from "@/i18n";
import { UNITS } from "@/data/site";
import { Reveal, MaskedLines } from "@/components/motion";
import UnitReviews from "@/components/UnitReviews";
import UnitAmenities from "@/components/UnitAmenities";
import Lightbox from "@/components/Lightbox";
import { usePageMeta } from "@/hooks/usePageMeta";
import StickyBook from "@/components/StickyBook";
import UnitStatus from "@/components/UnitStatus";
import FromPrice from "@/components/FromPrice";

// Every icon the admin panel offers for "Punti di forza" (admin/src/components/icons.js).
const ICONS = {
  Mountain, KeyRound, Car, ParkingCircle, ShieldCheck, Star, BedDouble, Wifi, Coffee, Bath, Waves, TreePine, Tv,
  Wind, MapPin, Clock, Users, Heart, Sun, Utensils, WashingMachine, Baby, PawPrint, Home, DoorOpen,
};

// Gallery: every photo, in blocks of four (one big, three small: 4 columns x
// 2 rows on desktop). A last block of 1-3 photos fills the same space, so the
// grid always closes cleanly.
const GRID = {
  1: ["col-span-2 md:col-span-4 row-span-2"],
  2: ["col-span-2 row-span-2", "col-span-2 row-span-2"],
  3: ["col-span-2 row-span-2", "col-span-2 row-span-1", "col-span-2 row-span-1"],
  4: ["col-span-2 row-span-2", "col-span-2 row-span-1", "col-span-1 row-span-1", "col-span-1 row-span-1"],
};

const UnitPage = ({ slug }) => {
  const { t, lang } = useLang();
  const unit = UNITS.find((u) => u.slug === slug);
  usePageMeta({ title: unit.seo.title[lang], description: unit.seo.description[lang] });
  const other = UNITS.find((u) => u.slug !== slug);
  const up = t.unitPage;
  const bookLabel = unit.bookable ? up.bookThis : (unit.availabilityStatus === "comingSoon" ? up.comingSoon : up.unavailable);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const gallery = (unit?.images || []).map((src, i, all) => ({ src, i, cls: GRID[Math.min(4, all.length - (i - (i % 4)))][i % 4] }));
  const openLightbox = (i) => {
    setLightboxIndex(i);
    setLightboxOpen(true);
  };

  return (
    <div data-testid={`unit-page-${slug}`} className={unit.bookable ? "pb-20 md:pb-0" : ""}>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-32 md:pt-40">
        <Reveal y={20}>
          <p className="text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-terracotta">{up.overline}</p>
        </Reveal>
        <h1 className="font-display font-medium text-5xl md:text-7xl tracking-tight mt-4" data-testid="unit-title">
          <MaskedLines lines={[unit.name]} delay={0.05} />
        </h1>
        <Reveal delay={0.12}>
          <UnitStatus unit={unit} className="mt-5" />
          <FromPrice unit={unit} className="mt-2" />
        </Reveal>
        <Reveal delay={0.15}>
          <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-5 text-sm text-stone" data-testid="unit-specs">
            <span className="inline-flex items-center gap-2" data-testid="unit-type"><Home className="h-4 w-4 text-terracotta" />{unit.airbnbType[lang]}</span>
            <span className="inline-flex items-center gap-2"><Users className="h-4 w-4 text-terracotta" />{unit.specs.guests} {up.guests}</span>
            <span className="inline-flex items-center gap-2"><BedDouble className="h-4 w-4 text-terracotta" />{unit.specs.bedrooms === 1 ? t.unitsPage.studio : `${unit.specs.bedrooms} ${up.bedrooms}`} · {unit.specs.beds} {up.beds}</span>
            <span className="inline-flex items-center gap-2"><Bath className="h-4 w-4 text-terracotta" />{unit.specs.bathrooms} {up.bathrooms}</span>
            {unit.specs.sqm && (
              <span className="inline-flex items-center gap-2"><Maximize className="h-4 w-4 text-terracotta" />{unit.specs.sqm} {up.sqm}</span>
            )}
            {unit.reviewsCount > 0 && (
              <span className="inline-flex items-center gap-2" data-testid="unit-rating">
                <Star className="h-4 w-4 fill-terracotta text-terracotta" />{unit.rating} · {unit.reviewsCount} {up.reviews}
              </span>
            )}
            {t.ecoBadge.enabled && (
              <span className="inline-flex items-center gap-2" data-testid="eco-badge">
                <Sun className="h-4 w-4 text-olive" />{t.ecoBadge.text}
              </span>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="grid sm:grid-cols-3 gap-4 mt-10" data-testid="unit-highlights">
            {unit.highlights.map((h, i) => {
              const Icon = ICONS[h.icon] || Star;
              return (
                <div key={i} className="rounded-xl border border-linen bg-card px-5 py-4 flex items-start gap-3" data-testid={`unit-highlight-${i}`}>
                  <Icon className="h-5 w-5 text-terracotta shrink-0 mt-0.5" />
                  <span className="text-sm text-ink/85">{h[lang]}</span>
                </div>
              );
            })}
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-12 md:mt-16 grid md:grid-cols-12 gap-10 items-end">
        <Reveal className="md:col-span-7">
          <button
            type="button"
            data-testid="unit-cover-open-lightbox"
            onClick={() => openLightbox(0)}
            aria-label={up.galleryTitle}
            className="relative block w-full overflow-hidden rounded-2xl aspect-[4/3] group"
          >
            {unit.cover ? (
              <img
                src={unit.cover}
                alt={unit.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : <div className="w-full h-full bg-sand" />}
            <span className="absolute bottom-4 right-4 flex items-center justify-center w-10 h-10 rounded-full bg-ink/60 text-cream opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <Expand className="h-4 w-4" />
            </span>
          </button>
        </Reveal>
        <Reveal className="md:col-span-5" delay={0.15}>
          <p className="text-xs uppercase tracking-[0.2em] text-stone">{unit.tagline[lang]}</p>
          <p className="mt-5 text-lg leading-relaxed font-light text-ink/85" data-testid="unit-description">
            {unit.description[lang]}
          </p>
          <div className="mt-7 space-y-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">{up.accessTitle}</p>
              <p className="mt-1.5 text-stone font-light leading-relaxed" data-testid="unit-access">{unit.guestAccess[lang]}</p>
            </div>
            {unit.otherNotes && (
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-terracotta font-semibold">{up.notesTitle}</p>
                <p className="mt-1.5 text-stone font-light leading-relaxed" data-testid="unit-notes">{unit.otherNotes[lang]}</p>
              </div>
            )}
          </div>
          {unit.bookable ? (
            <Link
              to={`/prenota/${unit.slug}`}
              data-testid="unit-book-button"
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-terracotta px-8 py-4 text-sm md:text-base font-medium text-cream hover:bg-terracotta-dark transition-colors duration-300"
            >
              {bookLabel}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          ) : (
            <span
              data-testid="unit-book-button"
              aria-disabled="true"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-linen px-8 py-4 text-sm md:text-base font-medium text-stone cursor-not-allowed"
            >
              {bookLabel}
            </span>
          )}
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32" data-testid="unit-sleeping">
        <Reveal>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">{up.sleepTitle}</h2>
        </Reveal>
        <div className={`grid gap-4 mt-8 md:mt-10 ${unit.sleeping.length === 1 ? "" : unit.sleeping.length === 2 || unit.sleeping.length === 4 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
          {unit.sleeping.map((s, i) => (
            <Reveal key={i} delay={i * 0.06} className="h-full">
              <div className="h-full flex items-center gap-4 rounded-2xl border border-linen bg-card px-5 py-5" data-testid={`sleeping-${i}`}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sand">
                  <BedDouble className="h-5 w-5 text-terracotta" aria-hidden="true" />
                </span>
                <div>
                  <p className="font-display text-xl md:text-2xl leading-tight">{s.room[lang]}</p>
                  <p className="mt-0.5 text-sm text-stone">{s.beds[lang]}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32" data-testid="unit-gallery">
        <Reveal>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">{up.galleryTitle}</h2>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[150px] md:auto-rows-[210px] gap-3 md:gap-4 mt-10">
          {gallery.map(({ src, i, cls }) => (
            <Reveal key={src} delay={(i % 4) * 0.06} className={cls}>
              <button
                type="button"
                data-testid={`unit-gallery-thumb-${i}`}
                onClick={() => openLightbox(i)}
                aria-label={unit.imageLabels[lang][i]}
                className="relative block overflow-hidden rounded-xl md:rounded-2xl w-full h-full group"
              >
                <img
                  src={src}
                  alt={unit.imageLabels[lang][i]}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors duration-300" />
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {unit.bookable && (
        <StickyBook
          to={`/prenota/${unit.slug}`}
          label={bookLabel}
          priceText={unit.fromPrice ? up.fromPrice.replace("{price}", unit.fromPrice) : null}
          watchTestId="unit-cta"
        />
      )}

      <Lightbox
        images={unit.images}
        labels={unit.imageLabels[lang]}
        startIndex={lightboxIndex}
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32 grid md:grid-cols-12 gap-14" data-testid="unit-amenities">
        <UnitAmenities unit={unit} up={up} lang={lang} />

        <div className="md:col-span-5 space-y-12">
          <Reveal delay={0.1}>
            <h3 className="font-display text-2xl md:text-3xl">{up.infoTitle}</h3>
            <div className="mt-6 space-y-5">
              {[
                { icon: Clock, label: up.checkin, value: up.checkinValue },
                { icon: Clock, label: up.checkout, value: up.checkoutValue },
                { icon: Users, label: up.maxGuests, value: String(unit.maxGuests) },
                { icon: KeyRound, label: up.selfCheckin, value: up.selfCheckinValue },
                ...unit.safety.map((s) => ({ icon: ShieldCheck, label: s[lang], value: "✓" })),
              ].map((row, i) => (
                <div key={i} className="flex items-center justify-between border-b border-linen pb-4">
                  <span className="flex items-center gap-3 text-stone text-sm">
                    <row.icon className="h-4 w-4 text-terracotta" />{row.label}
                  </span>
                  <span className="font-medium text-sm" data-testid={`unit-info-${i}`}>{row.value}</span>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <h3 className="font-display text-2xl md:text-3xl">{up.nearbyTitle}</h3>
            <ul className="mt-6 space-y-3">
              {up.nearby.map((n, i) => (
                <li key={i} className="flex items-center gap-3 text-stone text-sm">
                  <MapPin className="h-4 w-4 text-olive shrink-0" />{n}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {unit.reviewsCount > 0 && <UnitReviews unit={unit} up={up} lang={lang} />}

      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-24 md:mt-32" data-testid="unit-cta">
        <Reveal>
          <div className="rounded-[2rem] bg-terracotta text-cream px-8 md:px-16 py-14 md:py-20 flex flex-col md:flex-row md:items-end justify-between gap-10">
            <div className="max-w-xl">
              <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">{t.homeCta.title}</h2>
              <p className="mt-4 text-cream font-light">{t.homeCta.sub}</p>
            </div>
            {unit.bookable ? (
              <Link
                to={`/prenota/${unit.slug}`}
                data-testid="unit-cta-book-button"
                className="group shrink-0 self-start md:self-auto inline-flex items-center gap-3 rounded-full bg-cream text-ink px-8 py-4 font-medium hover:bg-sand transition-colors duration-300"
              >
                {bookLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
              </Link>
            ) : (
              <span
                data-testid="unit-cta-book-button"
                aria-disabled="true"
                className="shrink-0 inline-flex items-center gap-3 rounded-full bg-cream/40 text-cream/70 px-8 py-4 font-medium cursor-not-allowed"
              >
                {bookLabel}
              </span>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <Link to={other.path} data-testid="unit-other-link" className="group inline-flex items-center gap-3 text-ink/70 hover:text-terracotta transition-colors duration-300">
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1.5" />
            <span className="text-sm uppercase tracking-[0.2em]">{up.otherUnit}</span>
            <span className="font-display italic text-2xl">{other.name}</span>
          </Link>
        </Reveal>
      </section>
    </div>
  );
};

export default UnitPage;
